import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';
import Seo from '@/components/Seo';
import Turnstile from '@/components/Turnstile';
import { guardAuth, guardMessage, reportAuthFailure } from '@/lib/authGuard';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';


/**
 * Landing page for the password-recovery email link.
 * Supabase appends the recovery token to the URL hash
 * (#access_token=...&type=recovery). The supabase-js client picks it up and
 * establishes a temporary session, after which updateUser({ password }) works.
 */
const ResetPassword: React.FC = () => {
  const { t, path, lang } = useI18n();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState('');
  // Turnstile token — verified server-side before the password is changed.
  const [captcha, setCaptcha] = useState('');


  useEffect(() => {
    let alive = true;

    const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
    const params = new URLSearchParams(hash);
    const hashError = params.get('error_description') || params.get('error');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    const boot = async () => {
      if (hashError) {
        if (alive) { setValid(false); setReady(true); }
        return;
      }
      // Explicitly set the session from the hash when present (covers browsers
      // where detectSessionInUrl has not run yet).
      if (accessToken && refreshToken) {
        await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
      }
      const { data } = await supabase.auth.getSession();
      if (!alive) return;
      setValid(Boolean(data.session));
      setReady(true);
    };
    boot();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!alive) return;
      if (event === 'PASSWORD_RECOVERY' || session) {
        setValid(true);
        setReady(true);
        if (session?.user?.email) setEmail(session.user.email);
      }
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pw.length < 6) return setError(t('auth.errPassword'));
    if (pw !== pw2) return setError(t('auth.errPasswordMatch'));

    setBusy(true);

    // Bot + abuse gate on the recovery landing page: a leaked recovery link
    // should not be usable by an automated script, and repeated failures lock
    // the address out for 15 minutes.
    const verdict = await guardAuth('reset', email, captcha);
    if (!verdict.ok) {
      setBusy(false);
      return setError(guardMessage(verdict, t));
    }

    const { data, error: err } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (err) {
      await reportAuthFailure('reset', email);
      return setError(err.message);
    }

    if (data?.user?.id) {
      await supabase
        .from('mkt_activity')
        .insert({ user_id: data.user.id, action: 'password_changed', detail: 'reset-password-link' });
    }
    setDone(true);
    // Clear the recovery token from the address bar before moving on.
    window.history.replaceState(null, '', window.location.pathname);
    window.setTimeout(() => navigate(path('/dashboard')), 1200);
  };


  return (
    <div className="mx-auto max-w-md px-4 py-14 sm:px-6">
      <Seo title={t('auth.setNewTitle')} description={t('auth.setNewSub')} noindex />
      <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <div className="mb-5 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
            <KeyRound className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900">{t('auth.setNewTitle')}</h1>
            <p className="text-sm text-slate-500">{t('auth.setNewSub')}</p>
          </div>
        </div>

        {!ready && <p className="py-6 text-center text-sm text-slate-500">{t('common.loading')}</p>}

        {ready && !valid && !done && (
          <div>
            <p className="rounded-lg bg-amber-50 px-3 py-3 text-sm leading-relaxed text-amber-800">{t('auth.resetExpired')}</p>
            <Link
              to={path('/login')}
              className="mt-4 inline-block w-full rounded-xl bg-slate-900 px-6 py-3 text-center text-base font-bold text-white hover:bg-slate-800"
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        )}

        {ready && valid && !done && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className={labelCls}>{t('auth.newPassword')}</label>
              <input
                type="password"
                autoComplete="new-password"
                className={inputCls}
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(''); }}
              />
            </div>
            <div>
              <label className={labelCls}>{t('auth.confirmPassword')}</label>
              <input
                type="password"
                autoComplete="new-password"
                className={inputCls}
                value={pw2}
                onChange={(e) => { setPw2(e.target.value); setError(''); }}
              />
            </div>
            <Turnstile action="reset-password" onVerify={setCaptcha} label={t('auth.captchaLabel')} />
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-orange-500 px-6 py-3 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('auth.setNewCta')}
            </button>
            <Link to={path('/login')} className="block text-center text-sm font-semibold text-slate-500 hover:text-slate-900">
              {t('auth.backToLogin')}
            </Link>
          </form>
        )}

        {done && (
          <div className="py-4 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="mt-3 text-sm font-semibold text-emerald-700">{t('auth.passwordSaved')}</p>
            <p className="mt-1 text-xs text-slate-500">
              {lang === 'es' ? 'Redirigiendo a su panel…' : 'Redirecting to your dashboard…'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
